import { useState } from "react";
import { activeFilterTags, mrpLabel, priceLabel, toList } from "./catalog";
import { KioskIcon } from "./KioskIcon";

export const ringStyle = (bg, a1, a2, width = 1.5) => (a2
  ? { border: `${width}px solid transparent`, background: `linear-gradient(${bg}, ${bg}) padding-box, linear-gradient(90deg, ${a1} 0%, ${a1} 46%, ${a2} 54%, ${a2} 100%) border-box` }
  : { border: `${width}px solid ${a1}`, background: bg });

const clamp = lines => ({ display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" });

function Cover({ src, title, radius, shadow, style }) {
  const [broken, setBroken] = useState(false);
  const box = { width: "100%", height: "100%", borderRadius: radius, boxShadow: shadow ? "0 6px 18px rgba(0,0,0,0.28)" : "none", overflow: "hidden", flexShrink: 0, ...style };
  if (!src || broken) {
    return <div style={{ ...box, background: "linear-gradient(135deg,#cbd5e1,#94a3b8)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 28 }}>
      {(title || "?").trim().charAt(0).toUpperCase()}
    </div>;
  }
  return <div style={box}><img src={src} alt={title || ""} draggable={false} onError={() => setBroken(true)} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} /></div>;
}

export function ProductView({ node, products, catalog, vars, onVarChange, onSelect, interactive = true }) {
  const s = node.styles || {};
  const mode = node.productViewMode || "listing";
  const fs = s.fontSize || 16;
  const font = s.fontFamily || "Inter";
  const text = node.productTextColor || s.color || "#1f2937";
  const sub = node.productSubTextColor || "#6b7280";
  const accent = node.productAccentColor || "#d97706";
  const accent2 = node.productAccent2Color || "";
  const cardBg = node.productCardBg || "#ffffff";
  const radius = node.productCardRadius ?? 14;
  const gap = node.productGap ?? 20;
  const pad = node.productPadding ?? 16;
  const ratio = node.productImageRatio || 1.45;
  const showPrice = node.productShowPrice !== false;
  const tap = (kind, p) => e => { if (!interactive) return; e.stopPropagation(); onSelect?.(kind, p); };

  const wrap = { width: "100%", height: "100%", boxSizing: "border-box", padding: pad, overflowY: mode === "full" ? "hidden" : "auto", overflowX: "hidden", fontFamily: font, color: text, textAlign: "left", scrollbarWidth: "none", pointerEvents: interactive ? "auto" : "none" };

  const priceEl = (p, size = fs) => {
    if (!showPrice) return null;
    const label = priceLabel(p, catalog, node);
    if (!label) return null;
    const mrp = mrpLabel(p, catalog);
    return <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6, whiteSpace: "nowrap" }}>
      <span style={{ fontWeight: 700, fontSize: size, color: node.productPriceColor || text }}>{label}</span>
      {mrp && <span style={{ fontSize: size * 0.75, color: sub, textDecoration: "line-through" }}>{mrp}</span>}
    </span>;
  };

  const metaRow = (p, size) => (node.productShowMeta !== false && p.meta.length > 0)
    ? <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", fontSize: size, color: sub, minWidth: 0 }}>
        {p.meta.map((m, i) => <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
          {m.icon && <KioskIcon name={m.icon} size={size * 1.1} color={sub} />}
          {mode === "full" && m.label && <span style={{ opacity: 0.8 }}>{m.label}:</span>}
          <span>{m.value}</span>
        </span>)}
      </div>
    : null;

  const ctaBtn = (p, size) => {
    const label = p.ctaLabel || node.productCtaLabel;
    if (!label) return null;
    const ctaColor = node.productCtaColor || text;
    return <button onClick={tap("cta", p)} style={{ ...ringStyle(node.productCtaBg || "#ffffff", accent, accent2), color: ctaColor, borderRadius: 10, padding: `${size * 0.45}px ${size * 1.1}px`, fontSize: size, fontFamily: font, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 2px 6px rgba(0,0,0,0.08)", flexShrink: 0, display: "inline-flex", alignItems: "center", gap: size * 0.5 }}>
      {label}
      {node.productCtaIcon && <KioskIcon name={node.productCtaIcon} size={size * 1.1} color={ctaColor} />}
    </button>;
  };

  const tagsAll = activeFilterTags(node, vars);
  const varTags = node.productTagVar ? toList(vars?.[node.productTagVar]) : [];
  const chips = mode !== "full" && node.productShowFilterChips && tagsAll.length > 0
    ? <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: gap, fontSize: fs * 0.85 }}>
        <span style={{ color: text }}>{node.productFilterChipsLabel || "Showing results for"}</span>
        {tagsAll.map((t, i) => {
          const removable = varTags.includes(t) && onVarChange;
          return <span key={t + i} style={{ display: "inline-flex", alignItems: "center", gap: 8, ...ringStyle(cardBg, accent, accent2), borderRadius: 999, padding: "4px 12px", fontSize: fs * 0.8 }}>
            {t}
            {removable && <span onClick={e => { if (!interactive) return; e.stopPropagation(); onVarChange(node.productTagVar, varTags.filter(x => x !== t).join(",")); }} style={{ cursor: "pointer", opacity: 0.7, display: "inline-flex" }}><KioskIcon name="X" size={fs * 0.8} color={text} /></span>}
          </span>;
        })}
      </div>
    : null;

  if (mode === "full") {
    const p = products[0];
    if (!p) return <div style={{ ...wrap, display: "flex", alignItems: "center", justifyContent: "center", color: sub, fontSize: fs }}>{node.productEmptyText || "Select a product"}</div>;
    return <FullProduct key={p.id} p={p} node={node} wrap={wrap} fs={fs} text={text} sub={sub} accent={accent} accent2={accent2} cardBg={cardBg} radius={radius} gap={gap} ratio={ratio} priceEl={priceEl} metaRow={metaRow} ctaBtn={ctaBtn} />;
  }

  if (!products.length) {
    return <div style={wrap}>{chips}<div style={{ color: sub, fontSize: fs, textAlign: "center", paddingTop: 24 }}>{node.productEmptyText || "No products found"}</div></div>;
  }

  if (mode === "thumbnail") {
    const cols = Math.max(1, node.productColumns || 6);
    const limit = node.productLimit > 0 ? node.productLimit : 0;
    const visible = limit && products.length > limit ? products.slice(0, limit) : products;
    const rest = products.length - visible.length;
    const innerW = (s.width || 600) - pad * 2 - 1;
    const itemW = (innerW - gap * (cols - 1)) / cols;
    const imgH = itemW * ratio;
    return <div style={wrap}>
      {chips}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, ${itemW}px)`, gap }}>
        {visible.map(p => (
          <div key={p.id} onClick={tap("item", p)} style={{ cursor: interactive ? "pointer" : "default", minWidth: 0 }}>
            <div style={{ height: imgH }}><Cover src={p.image} title={p.title} radius={node.productThumbRadius ?? 4} shadow /></div>
            {node.productShowTitle && <div style={{ marginTop: 8, fontSize: fs * 0.85, fontWeight: 600, ...clamp(2) }}>{p.title}</div>}
            {node.productShowTitle && showPrice && <div style={{ marginTop: 2, fontSize: fs * 0.8 }}>{priceEl(p, fs * 0.8)}</div>}
          </div>
        ))}
        {rest > 0 && <div onClick={tap("more", null)} style={{ height: imgH, borderRadius: node.productThumbRadius ?? 4, ...ringStyle(cardBg, accent, accent2), boxSizing: "border-box", padding: 12, display: "flex", flexDirection: "column", justifyContent: "flex-start", cursor: interactive ? "pointer" : "default", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
          <span style={{ fontSize: fs * 1.3, fontWeight: 700 }}>+{rest}</span>
          <span style={{ fontSize: fs * 0.9, color: sub }}>{node.productMoreLabel || "More"}</span>
        </div>}
      </div>
    </div>;
  }

  const cols = Math.max(1, node.productColumns || 2);
  const imgW = node.productListImageWidth || 120;
  return <div style={wrap}>
    {chips}
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap }}>
      {products.map(p => (
        <div key={p.id} onClick={tap("item", p)} style={{ background: cardBg, borderRadius: radius, padding: fs, display: "flex", gap: fs * 1.1, boxShadow: "0 4px 18px rgba(0,0,0,0.08)", cursor: interactive ? "pointer" : "default", minWidth: 0, boxSizing: "border-box" }}>
          <div style={{ width: imgW, height: imgW * ratio, flexShrink: 0 }}><Cover src={p.image} title={p.title} radius={4} shadow /></div>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: fs * 0.5 }}>
            <div style={{ fontSize: fs * 1.25, fontWeight: 700, ...clamp(2) }}>{p.title}</div>
            {p.shortDesc && <div style={{ fontSize: fs * 0.8, color: sub, lineHeight: 1.45, ...clamp(node.productDescLines || 4) }}>{p.shortDesc}</div>}
            <div style={{ flex: 1 }} />
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 10 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                {metaRow(p, fs * 0.75)}
                {priceEl(p, fs)}
              </div>
              {ctaBtn(p, fs * 0.85)}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>;
}

function FullProduct({ p, node, wrap, fs, text, sub, accent, accent2, cardBg, radius, gap, ratio, priceEl, metaRow, ctaBtn }) {
  const gallery = [p.image, ...p.images].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
  const [active, setActive] = useState(0);
  const main = gallery[Math.min(active, gallery.length - 1)] || "";
  const s = node.styles || {};
  const stacked = (s.height || 0) > (s.width || 0) * 1.1;
  const imgW = stacked ? Math.min((s.width || 400) * 0.55, ((s.height || 600) * 0.4) / ratio) : Math.min((s.width || 800) * 0.34, ((s.height || 600) - 120) / ratio);
  return <div style={{ ...wrap, display: "flex", flexDirection: stacked ? "column" : "row", gap: gap * 1.5, alignItems: stacked ? "center" : "flex-start" }}>
    <div style={{ width: imgW, flexShrink: 0, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ height: imgW * ratio }}><Cover src={main} title={p.title} radius={6} shadow /></div>
      {gallery.length > 1 && <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {gallery.map((g, i) => (
          <div key={g + i} onClick={e => { e.stopPropagation(); setActive(i); }} style={{ width: 46, height: 46 * ratio * 0.7, borderRadius: 4, overflow: "hidden", outline: i === active ? `2px solid ${accent}` : "none", cursor: "pointer" }}>
            <img src={g} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
        ))}
      </div>}
    </div>
    <div style={{ flex: 1, minWidth: 0, alignSelf: "stretch", overflowY: "auto", scrollbarWidth: "none", display: "flex", flexDirection: "column", gap: fs * 0.8, background: node.productFullCard ? cardBg : "transparent", borderRadius: radius, padding: node.productFullCard ? fs * 1.25 : 0 }}>
      <div style={{ fontSize: fs * 2, fontWeight: 700, lineHeight: 1.2, color: text }}>{p.title}</div>
      {metaRow(p, fs * 0.9)}
      {priceEl(p, fs * 1.4)}
      {node.productShowTags !== false && p.tags.length > 0 && <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {p.tags.map(t => <span key={t} style={{ ...ringStyle(cardBg, accent, accent2, 1), color: text, borderRadius: 999, padding: "3px 12px", fontSize: fs * 0.75 }}>{t}</span>)}
      </div>}
      {p.shortDesc && <div style={{ fontSize: fs * 1.05, color: text, fontWeight: 500, lineHeight: 1.5 }}>{p.shortDesc}</div>}
      {p.description && <div style={{ fontSize: fs * 0.95, color: sub, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{p.description}</div>}
      <div style={{ display: "flex", gap: 12, paddingTop: 4 }}>{ctaBtn(p, fs)}</div>
    </div>
  </div>;
}
